export const PARTNER_NORMALIZATION_ISSUE_TYPES = Object.freeze({
  UNRECOGNIZED_MAKE: "unrecognized_make",
  UNRECOGNIZED_MODEL: "unrecognized_model",
  MISSING_TRIM: "missing_trim",
  AMBIGUOUS_TRIM: "ambiguous_trim",
  PARTIAL_NORMALIZATION: "partial_normalization",
  UNMATCHED_VEHICLE_IDENTITY: "unmatched_vehicle_identity",
  CONFLICTING_NORMALIZATION: "conflicting_normalization",
  LOW_CONFIDENCE_MATCH: "low_confidence_match",
  DUPLICATE_LOOKING_GROUP: "duplicate_looking_group",
  TITLE_POLLUTION: "title_pollution",
  AUTO_CORRECTED_IDENTITY: "auto_corrected_identity",
  LOOKUP_AMBIGUITY: "lookup_ambiguity",
  LOOKUP_NOT_FOUND: "lookup_not_found",
});

export const PARTNER_NORMALIZATION_ISSUE_LABELS = Object.freeze({
  [PARTNER_NORMALIZATION_ISSUE_TYPES.UNRECOGNIZED_MAKE]: "Unknown make",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.UNRECOGNIZED_MODEL]: "Unknown model",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.MISSING_TRIM]: "Trim missing",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.AMBIGUOUS_TRIM]: "Unclear trim",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.PARTIAL_NORMALIZATION]: "Incomplete match",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.UNMATCHED_VEHICLE_IDENTITY]: "No vehicle match",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.CONFLICTING_NORMALIZATION]: "Conflicting names",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.LOW_CONFIDENCE_MATCH]: "Low confidence",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.DUPLICATE_LOOKING_GROUP]: "Looks like a duplicate",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.TITLE_POLLUTION]: "Messy title",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.AUTO_CORRECTED_IDENTITY]: "Auto-corrected",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.LOOKUP_AMBIGUITY]: "Multiple possible matches",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.LOOKUP_NOT_FOUND]: "Not in catalog",
});

/** Plain-language explanation shown as the main problem on each card. */
export const PARTNER_NORMALIZATION_ISSUE_EXPLANATIONS = Object.freeze({
  [PARTNER_NORMALIZATION_ISSUE_TYPES.UNRECOGNIZED_MAKE]:
    "We don't recognize this make in the vehicle catalog.",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.UNRECOGNIZED_MODEL]:
    "The make is known, but this model doesn't match our catalog.",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.MISSING_TRIM]:
    "Make and model look fine, but trim is missing. Trim is optional but helps grouping.",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.AMBIGUOUS_TRIM]:
    "We found the make and model, but we're not sure which trim is correct.",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.PARTIAL_NORMALIZATION]:
    "This listing is only partly matched — some vehicle details still need confirmation.",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.UNMATCHED_VEHICLE_IDENTITY]:
    "We couldn't match this vehicle to anything in our catalog.",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.CONFLICTING_NORMALIZATION]:
    "The dealer text and our match disagree — someone should confirm the right name.",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.LOW_CONFIDENCE_MATCH]:
    "We have a guess, but we're not confident enough to trust it automatically.",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.DUPLICATE_LOOKING_GROUP]:
    "This may be the same vehicle listed under a slightly different name.",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.TITLE_POLLUTION]:
    "The listing title has extra words that make automatic matching harder.",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.AUTO_CORRECTED_IDENTITY]:
    "We auto-corrected the name — please confirm it looks right.",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.LOOKUP_AMBIGUITY]:
    "More than one vehicle in our catalog could fit this listing.",
  [PARTNER_NORMALIZATION_ISSUE_TYPES.LOOKUP_NOT_FOUND]:
    "Nothing in our catalog matches what the dealer wrote.",
});

const ISSUE_PRIORITY = [
  PARTNER_NORMALIZATION_ISSUE_TYPES.UNMATCHED_VEHICLE_IDENTITY,
  PARTNER_NORMALIZATION_ISSUE_TYPES.LOOKUP_NOT_FOUND,
  PARTNER_NORMALIZATION_ISSUE_TYPES.UNRECOGNIZED_MAKE,
  PARTNER_NORMALIZATION_ISSUE_TYPES.UNRECOGNIZED_MODEL,
  PARTNER_NORMALIZATION_ISSUE_TYPES.CONFLICTING_NORMALIZATION,
  PARTNER_NORMALIZATION_ISSUE_TYPES.LOOKUP_AMBIGUITY,
  PARTNER_NORMALIZATION_ISSUE_TYPES.AMBIGUOUS_TRIM,
  PARTNER_NORMALIZATION_ISSUE_TYPES.MISSING_TRIM,
  PARTNER_NORMALIZATION_ISSUE_TYPES.LOW_CONFIDENCE_MATCH,
  PARTNER_NORMALIZATION_ISSUE_TYPES.PARTIAL_NORMALIZATION,
  PARTNER_NORMALIZATION_ISSUE_TYPES.TITLE_POLLUTION,
  PARTNER_NORMALIZATION_ISSUE_TYPES.DUPLICATE_LOOKING_GROUP,
  PARTNER_NORMALIZATION_ISSUE_TYPES.AUTO_CORRECTED_IDENTITY,
];

export const PARTNER_NORMALIZATION_QUEUE_TABS = Object.freeze([
  {
    value: "",
    label: "To fix",
    description: "Listings that still need a confirmed vehicle name",
    summaryKey: "needsReview",
  },
  {
    value: "partial",
    label: "Partial match",
    description: "Make/model found, but trim or details are incomplete",
    summaryKey: "partial",
  },
  {
    value: "unmatched",
    label: "Not recognized",
    description: "We couldn't match the vehicle at all",
    summaryKey: "unmatched",
  },
  {
    value: "matched",
    label: "Confirmed",
    description: "Names already confirmed — review if something looks off",
    summaryKey: "matched",
  },
  {
    value: "all",
    label: "All listings",
    description: "Every listing regardless of match status",
    summaryKey: "total",
  },
]);

export const PARTNER_NORMALIZATION_STATUS_FILTERS = Object.freeze([
  { value: "", label: "To fix (default)" },
  { value: "partial", label: "Partial match" },
  { value: "unmatched", label: "Not recognized" },
  { value: "manual", label: "Manually set" },
  { value: "matched", label: "Confirmed" },
  { value: "all", label: "All statuses" },
]);

export const PARTNER_NORMALIZATION_SOURCE_FILTERS = Object.freeze([
  { value: "", label: "All sources" },
  { value: "portal", label: "Added in portal" },
  { value: "live", label: "Imported from dealer site" },
]);

export const PARTNER_NORMALIZATION_WORKFLOW_STEPS = Object.freeze([
  {
    step: 1,
    title: "Read what the dealer wrote",
    detail: "This is the raw text from the listing or import.",
  },
  {
    step: 2,
    title: "Compare with our match",
    detail: "See what Make, Model, and Trim we assigned.",
  },
  {
    step: 3,
    title: "Confirm or fix",
    detail: "Accept our suggestion or open the listing to correct it.",
  },
]);

export function partnerNormalizationIssueLabel(issueType) {
  return PARTNER_NORMALIZATION_ISSUE_LABELS[issueType] || issueType;
}

export function partnerNormalizationStatusLabel(status) {
  switch (status) {
    case "matched":
      return "Confirmed";
    case "partial":
      return "Partial match";
    case "unmatched":
      return "Not recognized";
    case "manual":
      return "Set manually";
    default:
      return status || "Unknown";
  }
}

export function partnerNormalizationPrimaryIssueExplanation(issueTypes = []) {
  if (!issueTypes.length) {
    return "This listing needs a quick name check before it can group correctly.";
  }

  const primary =
    ISSUE_PRIORITY.find((issueType) => issueTypes.includes(issueType)) || issueTypes[0];

  return (
    PARTNER_NORMALIZATION_ISSUE_EXPLANATIONS[primary] ||
    "This listing needs a quick name check before it can group correctly."
  );
}

export function partnerNormalizationQueueTab(value = "") {
  return (
    PARTNER_NORMALIZATION_QUEUE_TABS.find((tab) => tab.value === value) ||
    PARTNER_NORMALIZATION_QUEUE_TABS[0]
  );
}
